import { createContext, useContext, useEffect, useState } from "react";
import { api, clearDownloadToken } from "@/lib/api";

const AuthContext = createContext(null);

const ADMIN_ROLES = ["admin", "it_admin"];
const isAdminRole = (r) => ADMIN_ROLES.includes(r);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null); // null = checking, false = anonymous
  const [branch, setBranch] = useState(
    () => localStorage.getItem("dj_branch") || "DANDENONG"
  );

  // Staff accounts are tied to one store — the switcher never overrides that.
  const applyUser = (me) => {
    setUser(me);
    if (!isAdminRole(me?.role) && me?.branch) {
      localStorage.setItem("dj_branch", me.branch);
      setBranch(me.branch);
    }
    return me;
  };

  useEffect(() => {
    api
      .get("/auth/me")
      .then(({ data }) => applyUser(data))
      .catch(() => setUser(false));
  }, []);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    // Second factor: the caller shows the code screen and calls verifyCode next.
    if (data.mfa_required) return data;
    if (data.access_token) sessionStorage.setItem("dj_token", data.access_token);
    const { data: me } = await api.get("/auth/me");
    return applyUser(me);
  };

  const verifyCode = async (challenge, code, rememberDevice) => {
    const { data } = await api.post("/auth/verify-code", {
      challenge,
      code,
      remember_device: !!rememberDevice,
    });
    if (data.access_token) sessionStorage.setItem("dj_token", data.access_token);
    const { data: me } = await api.get("/auth/me");
    return applyUser(me);
  };

  const refreshUser = async () => {
    const { data: me } = await api.get("/auth/me");
    return applyUser(me);
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch (e) {
      /* ignore */
    }
    sessionStorage.removeItem("dj_token");
    clearDownloadToken();
    setUser(false);
  };

  const switchBranch = (b) => {
    if (user && !isAdminRole(user.role) && user.branch && b !== user.branch) return;
    localStorage.setItem("dj_branch", b);
    setBranch(b);
  };

  const can = (permission) => !!user?.permissions?.includes(permission);
  const branches = user?.branches || ["DANDENONG", "TRUGANINA"];

  // The shop closes at 8pm Melbourne time — nobody is left signed in past it, so a
  // counter left open overnight cannot be used by anyone who walks past it.
  useEffect(() => {
    if (!user) return undefined;
    const closeAt = () => {
      const now = new Date();
      const melbourne = new Date(
        now.toLocaleString("en-US", { timeZone: "Australia/Melbourne" })
      );
      const close = new Date(melbourne);
      close.setHours(20, 0, 0, 0);
      if (melbourne >= close) close.setDate(close.getDate() + 1);
      return close.getTime() - melbourne.getTime();
    };
    const timer = setTimeout(() => {
      logout();
    }, Math.max(1000, closeAt()));
    return () => clearTimeout(timer);
  }, [user]);

  return (
    <AuthContext.Provider
      value={{ user, login, verifyCode, refreshUser, logout, branch, branches, switchBranch, can, isAdmin: isAdminRole(user?.role) }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
